import { parseJson } from "Utils";
import _ from "lodash";
import update from "immutability-helper";
import ACLStorage from "lib/aclService"; // This might cause cyclic dependency, but its temporary
import { ROLE_VISITOR, STAFF } from "Constants/stringConstants";

import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { store } from "store/createStore";
import {
  getProjectGroupBasicDetails,
  getProjectGroupProgramDetails,
} from "./GraphqlHelpers";
import { CONTEXT_ENTITY_TYPE_ORDER_MAP } from "Utils/RnPHelpers";
import { getIsModuleSettingsV2Enabled } from "Utils/ACLStoreUtils";
import { getUserPrimaryGradeId } from "modules/CommonSelectors";

export const parsePreferenceValueObject = preferenceValueObject => {
  const { valueType, value } = preferenceValueObject || {};
  switch (valueType) {
    case "BOOLEAN":
      return value === "true";
    case "JSON":
    case "ARRAY":
    case "TEXT":
      return parseJson(value);
    default:
      return value;
  }
};

/**
 * Memoization is required to maintain the same reference of the result.
 */
export const getContextPayload = _.memoize(
  controlParameterContext => {
    const payload = [];

    /**
     * Curriculum program can appear in url as :curriculumProgramId or :schoolId
     */
    const isCurriculumProgramPresent =
      !!controlParameterContext["curriculumProgramId"] ||
      !!controlParameterContext["schoolId"];

    if (isCurriculumProgramPresent) {
      payload.push({
        entityType: "CURRICULUM_PROGRAM",
        entityId:
          controlParameterContext["curriculumProgramId"] ||
          controlParameterContext["schoolId"],
      });
    }

    const isLearningCoursePresent = !!controlParameterContext[
      "learningCourseId"
    ];

    if (isLearningCoursePresent) {
      const learningCourseId = controlParameterContext["learningCourseId"];

      payload.push({
        entityType: "LEARNING_COURSE",
        entityId: learningCourseId,
      });
    }

    const isCoursePresent =
      !!controlParameterContext["course_id"] ||
      !!controlParameterContext["courseId"];

    if (isCoursePresent) {
      const courseId =
        controlParameterContext["course_id"] ||
        controlParameterContext["courseId"];

      payload.push({
        entityType: "COURSE",
        entityId: courseId,
      });
    }

    return [payload];
  },
  params => JSON.stringify(params)
);

export const mergeVariablesWithDefaults = ({
  variables,
  defaultContextPayload,
  defaultControlParamterTypes,
  defaultPortalType,
}) => {
  return update(variables, {
    filters: {
      contexts: contexts => (contexts ? contexts : defaultContextPayload),
      controlParameterTypes: controlParameterTypes =>
        controlParameterTypes
          ? controlParameterTypes
          : defaultControlParamterTypes,
    },
    portalType: portalType => (portalType ? portalType : defaultPortalType),
  });
};

export const sortContext = context => {
  return _.sortBy(
    context,
    ({ entityType }) => CONTEXT_ENTITY_TYPE_ORDER_MAP[entityType] || 99 // 99 is the fallback value for unknown entity type to place it after defined types
  );
};

/**
 * @param {Array} contexts (Each entry in the array is an object with entityType and entityId)
 * @returns {string}
 */
export const getContextIdForRolesAndPermissions = contexts => {
  return ACLStorage.getContextIdFromContexts(sortContext(contexts));
};

/**
 * @param {Array} contexts (Each entry in the array is an object with entityType and entityId)
 * @returns {string}
 */
export const getContextIdForConfigurations = contexts => {
  let contextType;
  let contextsLocal = [];

  /**
   * These conditions are written for a payload which is wrt to RnP.
   * To use it for configurations, we need to transform the payload.
   */

  //Empty array in RnP means we are reading value wrt to ORGANIZATION + IDENTITY + USER.
  if (_.isEmpty(contexts)) {
    contextType = "ORGANIZATION__IDENTITY__USER";
  }

  //Array with only one object with entityType as CURRICULUM_PROGRAM means we are reading value wrt to CURRICULUM_PROGRAM + USER.
  if (
    _.size(contexts) === 1 &&
    contexts[0].entityType === "CURRICULUM_PROGRAM"
  ) {
    contextType = "CURRICULUM_PROGRAM__USER";
  }

  if (_.find(contexts, ({ entityType }) => entityType === "PROJECT")) {
    contextType = "PROJECT__USER";
  }

  if (_.find(contexts, ({ entityType }) => entityType === "LEARNING_COURSE")) {
    contextType = "LEARNING_COURSE__USER";
  }

  //If an array has an object with entityType as COURSE, then we are reading value wrt to COURSE + USER.
  if (_.find(contexts, ({ entityType }) => entityType === "COURSE")) {
    contextType = "COURSE__USER";
  }

  if (isUserVisitor()) {
    contextType = "ORGANIZATION__USER";
  }

  switch (contextType) {
    case "ORGANIZATION__IDENTITY__USER": {
      const userInfo = getUserInfo({
        portalType: "PLANNER",
      });

      const organizationId = userInfo.org_id;
      const userId = userInfo.id;
      const identityId = userInfo.identityId;

      /**
       * Note: The order of objects as well order of keys within the object is important.
       * It is kept the same as sent in the request.
       */
      contextsLocal = [
        { entityId: organizationId, entityType: "ORGANIZATION" },
        { entityId: userId, entityType: "USER" },
        { entityId: identityId, entityType: "IDENTITY" },
      ];
      break;
    }
    case "CURRICULUM_PROGRAM__USER": {
      const userInfo = getUserInfo({
        portalType: "PLANNER",
      });
      const userId = userInfo.id;
      const userType = userInfo.user_type;

      const gradeId = getUserPrimaryGradeId();

      const shouldIncludeGrade =
        getIsModuleSettingsV2Enabled() && userType !== STAFF;

      contextsLocal = [
        {
          entityId: contexts[0].entityId,
          entityType: "CURRICULUM_PROGRAM",
        },
        ...(shouldIncludeGrade
          ? [{ entityId: gradeId, entityType: "GRADE" }]
          : []),
        {
          entityId: userId,
          entityType: "USER",
        },
      ];

      break;
    }

    case "COURSE__USER": {
      const userInfo = getUserInfo({
        portalType: "PLANNER",
      });
      const userId = userInfo.id;

      const courseIndex = _.findIndex(
        contexts,
        ({ entityType }) => entityType === "COURSE"
      );

      contextsLocal = [];

      contextsLocal.push(
        {
          entityId: contexts[courseIndex].entityId,
          entityType: "COURSE",
        },
        {
          entityId: userId,
          entityType: "USER",
        }
      );
      break;
    }

    case "LEARNING_COURSE__USER": {
      const userInfo = getUserInfo({
        portalType: "PLANNER",
      });
      const userId = userInfo.id;

      const learningCourseIndex = _.findIndex(
        contexts,
        ({ entityType }) => entityType === "LEARNING_COURSE"
      );

      contextsLocal = [
        {
          entityId: contexts[learningCourseIndex].entityId,
          entityType: "LEARNING_COURSE",
        },
        {
          entityId: userId,
          entityType: "USER",
        },
      ];
      break;
    }

    case "PROJECT__USER": {
      const userInfo = getUserInfo({
        portalType: "PLANNER",
      });
      const userId = userInfo.id;
      const userType = userInfo.user_type;
      const curriculumProgramId = _getCurriculumProgramIdFromProjectGroupId(
        contexts[0].entityId
      );
      const gradeId = getUserPrimaryGradeId();

      const shouldIncludeGrade =
        getIsModuleSettingsV2Enabled() && userType !== STAFF;

      contextsLocal = [
        {
          entityId: curriculumProgramId,
          entityType: "CURRICULUM_PROGRAM",
        },
        ...(shouldIncludeGrade
          ? [{ entityId: gradeId, entityType: "GRADE" }]
          : []),
        {
          entityId: userId,
          entityType: "USER",
        },
      ];

      break;
    }

    case "ORGANIZATION__USER": {
      const userInfo = getUserInfo({
        portalType: "PLANNER",
      });

      const organizationId = userInfo.org_id;

      const userId = userInfo.id;

      contextsLocal = [
        { entityId: organizationId, entityType: "ORGANIZATION" },
        { entityId: userId, entityType: "USER" },
      ];
      break;
    }

    default: {
      contextsLocal = [];
    }
  }

  return _.map(
    contextsLocal,
    ({ entityType, entityId }) => `${entityType}::${entityId}`
  ).join("::");
};

export const updateContextsAndContextIdMap = ({ queryResponse, contexts }) => {
  const length = _.size(contexts);
  for (let i = 0; i < length; i++) {
    const contextId = queryResponse[i].uid;
    ACLStorage.updateContextsAndContextIdMap(
      //Sorting is required to avoid ambiguity of order of elements in context array.
      sortContext(contexts[i]),
      contextId
    );
  }
};

export const getProgramTypeFromLocalStorage = () => {
  const state = store.getState();

  const currentCurriculumProgram = _.get(
    state,
    "platform.currentCurriculumProgram",
    null
  );

  return currentCurriculumProgram ? currentCurriculumProgram.type : "";
};

export const getProgramTypeFromLocalStorageBasedOnContext = contexts => {
  let curriculumProgramId;

  if (_.find(contexts, ({ entityType }) => entityType === "PROJECT")) {
    const projectGroupId =
      _.find(contexts, ({ entityType }) => entityType === "PROJECT")
        ?.entityId ?? "";
    curriculumProgramId = _getCurriculumProgramIdFromProjectGroupId(
      projectGroupId
    );
  } else {
    curriculumProgramId =
      _.find(contexts, ({ entityType }) => entityType === "CURRICULUM_PROGRAM")
        ?.entityId ?? "";
  }

  if (!curriculumProgramId) {
    return "";
  }

  const state = store.getState();

  const organizationCurriculumPrograms = _.get(
    state,
    "platform.organizationCurriculumPrograms",
    []
  );

  const program = _.find(
    organizationCurriculumPrograms,
    ({ id }) => id === curriculumProgramId
  );

  return program ? program.type : "";
};

export const getProjectGroupTypeFromRedux = () => {
  const state = store.getState();
  return _.get(state, "project.projectGroupDetails.type", "");
};

export const getProjectGroupTypeBasedOnContext = contexts => {
  const projectGroupId =
    _.find(contexts, ({ entityType }) => entityType === "PROJECT")?.entityId ??
    "";
  if (!projectGroupId) {
    return "";
  }
  const projectGroupDetails = getProjectGroupBasicDetails(projectGroupId);
  return projectGroupDetails ? projectGroupDetails.type : "";
};

const _getCurriculumProgramIdFromProjectGroupId = projectGroupId => {
  const projectGroupDetails = getProjectGroupProgramDetails(projectGroupId);

  return _.get(projectGroupDetails, "curriculumProgram.id", "");
};

export const isUserVisitor = () => {
  const userInfo = getUserInfo();

  return _.isEqual(userInfo.role, ROLE_VISITOR);
};

export const areContextsValid = ({
  contextsPassedFromComponent = [],
  defaultContextPayload,
}) => {
  // If context is not passed then we can consider it as valid
  if (
    _.isNil(contextsPassedFromComponent) ||
    _.isEmpty(contextsPassedFromComponent)
  ) {
    return true;
  }

  /**
   * If component provides multiple contexts (array length > 1),
   * we don't set up context anywhere, so return false immediately.
   * Default context always has only one array inside contexts array.
   */

  if (_.size(contextsPassedFromComponent) > 1) {
    return false;
  }

  /**
   * At this point, we know both contextsPassedFromComponent and defaultContextPayload
   * have exactly one element (single context array).
   * Compare the first (and only) context array from each.
   */

  const componentContext = _.head(contextsPassedFromComponent);
  const defaultContext = _.head(defaultContextPayload);

  if (_.size(componentContext) !== _.size(defaultContext)) {
    return false;
  }

  // Sort contexts by entityType for consistent comparison
  const sortedComponentContext = _.sortBy(componentContext, "entityType");
  const sortedDefaultContext = _.sortBy(defaultContext, "entityType");

  // Compare each entity in the contexts
  return _.every(sortedComponentContext, (entity, index) => {
    const defaultContextEntity = sortedDefaultContext[index];
    return (
      entity.entityType === defaultContextEntity.entityType &&
      entity.entityId === defaultContextEntity.entityId
    );
  });
};
